import { useFormik } from 'formik';
import { Filter, RefreshCw, Search } from 'lucide-react';
import styled from 'styled-components';

import { Button } from '../../../../../../components/atoms/button/button';
import { Input } from '../../../../../../components/atoms/input/input';
import { Select } from '../../../../../../components/atoms/select/select';
import {
  ClienteProdutosFilters,
  TIPO_PRODUTO_LABEL,
  TipoProduto,
} from '../../../../../../types/cliente-produto';

interface Props {
  initialValues: ClienteProdutosFilters;
  onApply: (values: ClienteProdutosFilters) => void;
  onClear: () => void;
}

const Wrapper = styled.section`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const Actions = styled.div`
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const statusOptions = [
  { label: 'Todos', value: '' },
  { label: 'Ativos', value: 'true' },
  { label: 'Inativos', value: 'false' },
];

const tipoOptions = [
  { label: 'Todos', value: '' },
  ...Object.entries(TIPO_PRODUTO_LABEL).map(([value, label]) => ({
    label,
    value,
  })),
];

const recorrenciaOptions = [
  { label: 'Todos', value: '' },
  { label: 'Recorrentes', value: 'true' },
  { label: 'Não recorrentes', value: 'false' },
];

const toBoolean = (raw: string): boolean | '' =>
  raw === '' ? '' : raw === 'true';

export const ProdutosFilters = ({ initialValues, onApply, onClear }: Props) => {
  const formik = useFormik<ClienteProdutosFilters>({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => onApply(values),
  });

  return (
    <Wrapper
      as="form"
      onSubmit={formik.handleSubmit}
      aria-label="Filtros de produtos do cliente"
    >
      <Field>
        Busca por nome
        <Input
          name="search"
          placeholder="Ex: Curso Avançado"
          value={formik.values.search ?? ''}
          onChange={formik.handleChange}
          startIcon={<Search size={16} />}
        />
      </Field>

      <Field>
        Status
        <Select
          options={statusOptions}
          name="ativo"
          value={
            formik.values.ativo === undefined || formik.values.ativo === ''
              ? ''
              : formik.values.ativo
                ? 'true'
                : 'false'
          }
          onChange={(e) => formik.setFieldValue('ativo', toBoolean(e.target.value))}
        />
      </Field>

      <Field>
        Tipo
        <Select
          options={tipoOptions}
          name="tipo"
          value={(formik.values.tipo ?? '').toString()}
          onChange={(e) =>
            formik.setFieldValue(
              'tipo',
              e.target.value === ''
                ? ''
                : (Number(e.target.value) as TipoProduto),
            )
          }
        />
      </Field>

      <Field>
        Recorrente
        <Select
          options={recorrenciaOptions}
          name="recorrencia"
          value={
            formik.values.recorrencia === undefined ||
            formik.values.recorrencia === ''
              ? ''
              : formik.values.recorrencia
                ? 'true'
                : 'false'
          }
          onChange={(e) =>
            formik.setFieldValue('recorrencia', toBoolean(e.target.value))
          }
        />
      </Field>

      <Actions>
        <Button
          variant="ghost"
          type="button"
          startIcon={<RefreshCw size={14} />}
          onClick={() => {
            formik.resetForm({ values: {} });
            onClear();
          }}
        >
          Limpar
        </Button>
        <Button
          variant="primary"
          type="submit"
          startIcon={<Filter size={14} />}
        >
          Aplicar filtros
        </Button>
      </Actions>
    </Wrapper>
  );
};
